import { createFileRoute } from "@tanstack/react-router";
import {
  listParam,
  listValue,
  optionParam,
  textParam,
} from "@/utils/searchParams";
import { ASSESSMENT_SCOPES } from "@/utils/assessmentScope";
import type { AssessmentScope, GroupContractStatus } from "@/hooks/types";

// The filters and search live in the URL, so opening a product and coming back
// lands on the list the reviewer was reading rather than a reset one (Jira NAV-45).

/**
 * Where a product stands on a group contract — one axis, one control.
 *
 * The first three are derived rather than stored, and they are the questions
 * the page used to answer with tabs above the filter row. A tab strip and a
 * filter that both narrow the same list are two controls for one decision:
 * the reader has to work out which of them is in charge, and the combinations
 * ("pipeline" tab + "No group contract" filter) can only ever be empty. So
 * they collapse into the values here, and the list below is always every tool.
 */
export const PRODUCT_CONTRACT_FILTERS = [
  /** No contract yet and something arguing for one — the candidate list. */
  "CANDIDATE",
  /** Anywhere in Legal's pipeline, whichever of the four statuses. */
  "IN_PIPELINE",
  /** Never taken up: no contract, whether or not anything argues for one. */
  "NONE",
  "PENDING_LEGAL_REVIEW",
  "IN_NEGOTIATION",
  "CONTRACTED",
  "DECLINED",
] as const satisfies readonly (
  "CANDIDATE" | "IN_PIPELINE" | "NONE" | GroupContractStatus
)[];
export type ProductContractFilter = (typeof PRODUCT_CONTRACT_FILTERS)[number];

/**
 * Both orders name something the reader can count on the screen in front of
 * them — units paying separately, or the alphabet.
 *
 * That is the constraint worth keeping: an order by *number of derived
 * signals* ranks candidates just as well and explains itself to nobody, since
 * "reasons" is a quantity the page never shows. The strongest candidates are
 * the ones the most units pay for separately regardless, so `business units` carries
 * the same ranking under a name that points at something visible, with signal
 * count as its tiebreaker.
 */
export const PRODUCT_SORTS = ["businessUnits", "name"] as const;
export type ProductSort = (typeof PRODUCT_SORTS)[number];

/**
 * How several selected business units combine.
 *
 * `ANY` is what a multi-select ordinarily means and is the default. `ALL` is the
 * question the filter was actually asked for: "the tools all of them hold" is
 * where two business units overlap, which is the case for one contract instead of two
 * (Jira NAV-85). They are different questions and the second one is not
 * reachable by selecting units one at a time, so both are offered — and the
 * control only appears once more than one business unit is selected, since with one
 * they are the same filter.
 */
export const PRODUCT_BU_MATCHES = ["ANY", "ALL"] as const;
export type ProductBuMatch = (typeof PRODUCT_BU_MATCHES)[number];

export type GroupLegalProductsSearch = {
  q?: string;
  /**
   * Business unit ids, comma-separated — `bu-chaptr,bu-holtzbrinck`.
   *
   * A string rather than an array, because that is what is actually in the URL:
   * declaring an array here would have the router JSON-encode it, and the point
   * of these params is a link a reviewer can read and edit. `listParam` turns it
   * back into ids on both sides, so the splitting rule lives in one place.
   *
   * The choices are the units that exist, so there is no fixed list to validate
   * against: the "All" sentinel is kept out and an unknown id filters to
   * nothing, which the page then says.
   */
  bu?: string;
  buMatch?: ProductBuMatch;
  contract?: ProductContractFilter;
  /** Products one of whose standing conclusions was given under this limit
   *  (Jira NAV-220) — "which tools are approved for browser use only". */
  scope?: AssessmentScope;
  sort?: ProductSort;
};

/** Shared with the moved address `/group-legal/products`, which validates the
 *  same params in order to forward them (see `movedRoutes.ts`). */
export function groupLegalToolsSearch(
  search: Record<string, unknown>,
): GroupLegalProductsSearch {
  return {
    q: textParam(search.q),
    // Normalised on the way in — deduped, emptied of blanks — so the page can
    // trust it and a hand-edited `?bu=bu-a,,bu-a` does not filter twice.
    bu: listValue(listParam(search.bu) ?? []),
    buMatch: optionParam(PRODUCT_BU_MATCHES, search.buMatch),
    contract: optionParam(PRODUCT_CONTRACT_FILTERS, search.contract),
    scope: optionParam(ASSESSMENT_SCOPES, search.scope),
    sort: optionParam(PRODUCT_SORTS, search.sort),
  };
}

export const Route = createFileRoute("/group-legal/tools")({
  validateSearch: groupLegalToolsSearch,
});
